import React from 'react';
import {
  AssistantMessage,
  AssistantToolCall,
} from 'components/framework/models';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import AssistantMessageComponent from './AssistantMessage';
import UserMessage from './UserMessage';
import Reasoning from './Reasoning';
import ToolCall from './ToolCall';
import styles from './ConversationPanel.module.css';
import { FROZEN_CHAT_ERROR } from 'components/Assistant/constants';
import NextActionSuggestionsPanel from '../SuggestionsPanel/NextActionSuggestionsPanel';

// Extract next actions from both old and new formats
export const extractNextActions = (text: string): Record<string, string> => {
  const actions: Record<string, string> = {};

  // Format: #### Next Steps or Next Actions followed by bullet points
  // Match the Next Actions or Next Steps section with variable number of hashtags
  const nextActionsRegex =
    /(?:\s*-{1,}\s*)?\s*#{1,5}\s*Next\s+(?:Actions|Steps)[\s\S]*?(?=(?:#{1,5}\s*[^\n]+)|$)/i;
  const nextActionsMatch = text.match(nextActionsRegex);

  if (nextActionsMatch) {
    const actionsSection = nextActionsMatch[0];
    // Match bullet points with different symbols (-, *, •) but not consecutive ones like ---
    // New format: "- Short imperative phrase: Concise instructions"
    const bulletPointRegex =
      /(^|\n)\s*[-*•](?![-*•])\s*([^:\n]+)(?:\s*:\s*([^\n]*))?/g;
    let bulletMatch;

    while ((bulletMatch = bulletPointRegex.exec(actionsSection)) !== null) {
      const fullText = bulletMatch[2].trim();
      const instruction = bulletMatch[3]?.trim();

      if (instruction) {
        // Correct format: "Short imperative phrase: Concise instructions"
        actions[fullText] = instruction;
      } else {
        // Fallback format: "Short imperative phrase"
        actions[fullText] = fullText;
      }
    }
  }

  return actions;
};

type ToolCallWithMessageId = { toolCall: AssistantToolCall; messageId: string };
type ReasoningItem = { reasoning: string; hasContent: boolean };
type RenderableItem = AssistantMessage | ToolCallWithMessageId | ReasoningItem;

// Type guard functions
const isAssistantMessage = (item: RenderableItem): item is AssistantMessage => {
  return AssistantMessage.is(item as any);
};

const isToolCallWithMessageId = (
  item: RenderableItem
): item is ToolCallWithMessageId => {
  return (
    'toolCall' in item &&
    'messageId' in item &&
    AssistantToolCall.is((item as any).toolCall)
  );
};

const isReasoningItem = (item: RenderableItem): item is ReasoningItem => {
  return 'reasoning' in item && 'hasContent' in item;
};

// Helper function to flatten messages into individual renderable items
export const flattenMessagesForRendering = (
  messages: AssistantMessage[]
): RenderableItem[] => {
  const items: RenderableItem[] = [];

  messages.forEach(message => {
    if (message.role === 'user') {
      items.push(message);
    } else {
      if (message.reasoning) {
        items.push({
          reasoning: message.reasoning,
          hasContent:
            !message.active ||
            message.content.length > 0 ||
            message.toolCalls.length > 0,
        });
      }
      if (message.toolCalls?.length > 0) {
        // Add message ID to each tool call for grouping
        const toolCallsWithMessageId: ToolCallWithMessageId[] =
          message.toolCalls.map(toolCall => ({
            toolCall,
            messageId: message.id,
          }));
        items.push(...toolCallsWithMessageId);
      }
      if (message.content) {
        items.push(message);
      }
    }
  });

  return items;
};

type GroupableItem = RenderableItem;
type GroupedItem = GroupableItem | GroupableItem[];

// Helper function to group items for display
// Groups collapsible tool calls and reasoning together, and groups tool calls by message ID
export const groupItemsForDisplay = (
  items: RenderableItem[]
): GroupedItem[] => {
  const groups: GroupedItem[] = [];
  let currentCollapsibleGroup: GroupableItem[] = [];

  const shouldStartNewGroup = (item: RenderableItem) => {
    return (
      (isAssistantMessage(item) && item.role === 'user') ||
      (isAssistantMessage(item) && item.content) ||
      (isToolCallWithMessageId(item) && item.toolCall.collapsable !== true)
    );
  };

  // First pass: group collapsible items
  items.forEach(item => {
    if (shouldStartNewGroup(item)) {
      // Close current group if it has items
      if (currentCollapsibleGroup.length > 0) {
        groups.push(currentCollapsibleGroup);
        currentCollapsibleGroup = [];
      }
      groups.push(item);
    } else {
      // Add to collapsible group
      currentCollapsibleGroup.push(item);
    }
  });

  // Don't forget the last group
  if (currentCollapsibleGroup.length > 0) {
    groups.push(currentCollapsibleGroup);
  }

  // Second pass: group non-collapsible tool calls by message ID
  const finalGroups: GroupedItem[] = [];

  groups.forEach(group => {
    if (
      !Array.isArray(group) &&
      isToolCallWithMessageId(group) &&
      group.toolCall.collapsable !== true
    ) {
      // Check if we can group this with the previous group
      const lastGroup = finalGroups[finalGroups.length - 1];
      if (
        Array.isArray(lastGroup) &&
        lastGroup.length > 0 &&
        isToolCallWithMessageId(lastGroup[0]) &&
        lastGroup[0].toolCall.collapsable !== true &&
        lastGroup[0].messageId === group.messageId
      ) {
        // Add to existing tool call group
        lastGroup.push(group);
      } else if (
        !Array.isArray(lastGroup) &&
        isToolCallWithMessageId(lastGroup) &&
        lastGroup.toolCall.collapsable !== true &&
        lastGroup.messageId === group.messageId
      ) {
        // Convert single tool call to group and add new one
        finalGroups[finalGroups.length - 1] = [lastGroup, group];
      } else {
        // Start new group
        finalGroups.push(group);
      }
    } else {
      finalGroups.push(group);
    }
  });

  return finalGroups;
};

// Helper function to render message groups as React components
export const renderMessageGroups = (
  controller: AssistantViewController,
  groups: GroupedItem[],
  readOnly: boolean
) => {
  const groupCount = groups.length || 0;

  return groups.map((group, index) => {
    // Single user message
    if (
      !Array.isArray(group) &&
      isAssistantMessage(group) &&
      group.role === 'user'
    ) {
      return <UserMessage key={group.uniqueId} message={group} />;
    }

    // Single assistant message with content
    if (
      !Array.isArray(group) &&
      isAssistantMessage(group) &&
      group.role === 'assistant'
    ) {
      let suggestions: Record<string, string> = {};
      if (!readOnly) {
        suggestions = extractNextActions(group.content);
      }

      return (
        <React.Fragment key={group.uniqueId}>
          <AssistantMessageComponent message={group} />
          {Object.keys(suggestions).length > 0 && (
            <NextActionSuggestionsPanel
              suggestions={suggestions}
              afterMessage={group}
              collapsible
              collapsed={groupCount - 1 !== index}
            />
          )}
        </React.Fragment>
      );
    }

    // Single standalone tool call
    if (!Array.isArray(group) && isToolCallWithMessageId(group)) {
      return (
        <ToolCall
          key={group.toolCall.id}
          toolCall={group.toolCall}
          isGrouped={false}
        />
      );
    }

    // Group of items (tool calls and/or reasoning)
    if (Array.isArray(group)) {
      // Determine if this is a group of non-collapsible tool calls
      const isToolCallGroup = group.every(
        (item: GroupableItem) =>
          isToolCallWithMessageId(item) && item.toolCall.collapsable !== true
      );

      const groupedComponents = group.map((item: GroupableItem, itemIndex) => {
        if (isToolCallWithMessageId(item)) {
          return (
            <ToolCall
              key={item.toolCall.id}
              toolCall={item.toolCall}
              isGrouped={!isToolCallGroup}
            />
          );
        } else if (isReasoningItem(item)) {
          return (
            <Reasoning
              key={itemIndex}
              reasoning={item.reasoning}
              hasContent={item.hasContent}
            />
          );
        }
        return null;
      });

      let approvalAllButtons: React.ReactElement | null = null;
      if (isToolCallGroup) {
        const toolCallsPendingApproval = group.filter(
          (toolCall: GroupableItem): toolCall is ToolCallWithMessageId =>
            isToolCallWithMessageId(toolCall) &&
            toolCall.toolCall.status === 'needs_approval'
        );

        if (toolCallsPendingApproval.length > 1) {
          approvalAllButtons = (
            <div className={styles.bulkToolCallActions}>
              <aha-button
                kind='plain'
                size='mini'
                {...(controller.model.isChatFrozen ? { disabled: true } : {})}
                onClick={() =>
                  controller.actionRejectAllToolCalls(
                    toolCallsPendingApproval.map(tc => tc.toolCall)
                  )
                }
              >
                Ignore
              </aha-button>
              {controller.model.isChatFrozen && (
                <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
              )}
              <aha-button
                size='mini'
                kind='primary'
                {...(controller.model.isChatFrozen ? { disabled: true } : {})}
                onClick={() =>
                  controller.actionApproveAllToolCalls(
                    toolCallsPendingApproval.map(tc => ({
                      toolCall: tc.toolCall,
                    }))
                  )
                }
              >
                Approve all
              </aha-button>
              {controller.model.isChatFrozen && (
                <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
              )}
            </div>
          );
        }
      }

      return (
        <div
          className={
            isToolCallGroup ? styles.bulkToolCalls : styles.groupedToolCalls
          }
          key={index}
        >
          {groupedComponents}
          {approvalAllButtons}
        </div>
      );
    }

    // Single reasoning item
    if (!Array.isArray(group) && isReasoningItem(group)) {
      return (
        <Reasoning
          key={index}
          reasoning={group.reasoning}
          hasContent={group.hasContent}
        />
      );
    }

    return null;
  });
};
